import ReactEcharts from "echarts-for-react";
import { useEffect, useRef, useState } from "react";
import { Container, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import api from "src/store/features/api";
import { TreeMapData } from "src/components/markets/Charts/types/index";
import { TreeMapSkelton } from "src/components/common/Skeletons";
import { useTreeMapFilterState } from "src/store/features/MarketTreeMapFilter.slice";
import MarketTreeMapToolBar from "./MarketTreeMapToolBar";
import { getLevelOption, percentageChecker } from "./MarketTreeMapHelpers";
import SettingsIcon from "@mui/icons-material/Settings";
import { PrimarySector, SecondarySector, TreeMapToken } from "src/store/features/api/generated";
import { MarketsTreemapExplainer } from "./MarketTreemapExplainer";
import MarketTreeMapLegend from "./MarketTreeMapLegend";
import { trackEvent } from "src/Amplitude";
import TreemapScreenshotButton from "./MarketTreeMapScreenshotButton";

const MarketTreeMap = () => {
  const echartRef = useRef(null);
  // ------------------Style Sheet---------------------------
  const theme = useTheme();
  // ------------------State---------------------------
  const [treeMapData, setTreeMapData] = useState<undefined | any>();
  const [sectorSpilt, setSectorSplit] = useState<boolean>(false);
  const [dropDownData, setDropDownData] = useState<any>([]);
  const [toolTipTitle, setToolTipTitle] = useState<string>();
  const [toolTipSubTitle, setToolTipSubTitle] = useState<string>("Market Cap");
  const [roamingEnabled, setRoamingEnabled] = useState<boolean>(true);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const settingsDesc = "Adjust chart settings";
  // ------------------_REDUX---------------------------
  const { timeFrame, hideCoins, filterValue } = useTreeMapFilterState();
  const { data, isLoading } = api.useGetTreeMapQuery();
  // ------------------Effects---------------------------

  useEffect(() => {
    trackEvent("Visit_Page", { page: "Token-map" });
  }, []);

  useEffect(() => {
    if (!data) return undefined;
    const minValue = Math.min(
      ...data.flatMap((item) =>
        item.marketCapDominance.sectors.flatMap((sector) =>
          sector.tokens.map((token) => token.performanceValue),
        ),
      ),
    );
    const maxValue = Math.max(
      ...data.flatMap((item) =>
        item.marketCapDominance.sectors.flatMap((sector) =>
          sector.tokens.map((token) => token.performanceValue),
        ),
      ),
    );

    const scaleValue = (value: any) => {
      // Apply logarithmic scaling
      return (Math.log(value - minValue + 1) / Math.log(maxValue - minValue + 1)) * 100;
    };

    if (data) {
      const newTreeMapData = data?.map((item: any, idx: number) => {
        const performanceData: PrimarySector = item.marketCapDominance;

        const performanceValue = performanceData.value;

        const name = item.name;
        const allPrimarySectorTokens = ([] as any[]).concat(
          ...performanceData.sectors.map((x) => x.tokens),
        );

        // Market data overview with no sub-sectors
        const coinData = allPrimarySectorTokens
          ?.filter(
            (obj) =>
              obj.performanceValue > filterValue[0] && obj.performanceValue <= filterValue[1],
          )
          .map((item: TreeMapToken, key: number) => {
            let change;

            switch (timeFrame) {
              case "Daily":
                change = item.daily;
                setToolTipTitle("1D change");
                break;
              case "Weekly":
                change = item.weekly;
                setToolTipTitle("1W change");
                break;
              case "Monthly":
                change = item.monthly;
                setToolTipTitle("1M change");
                break;
            }

            return {
              value: item.performanceValue,
              name: item.currencyConfig.code,
              logo: item.currencyConfig.logoUrl,
              sym: item.currencyConfig.code,
              link: `/coin/${item.currencyConfig.code}`,
              label: labelStyles,
              change,
              itemStyle: {
                color: percentageChecker(change ?? 0, timeFrame),
              },
            };
          });
        // Market data overview  split into subsectors.
        const coinBreakdownData = performanceData.sectors?.map(
          (item: SecondarySector, key: number) => {
            const sectorValue = item.value;
            let change: any;

            const sectorData = item.tokens
              ?.filter(
                (obj) =>
                  obj.performanceValue > filterValue[0] && obj.performanceValue <= filterValue[1],
              )
              .map((item: any, ind: number) => {
                switch (timeFrame) {
                  case "Daily":
                    change = item.daily;
                    setToolTipTitle("1D change");
                    break;
                  case "Weekly":
                    change = item.weekly;
                    setToolTipTitle("1W change");
                    break;
                  case "Monthly":
                    change = item.monthly;
                    setToolTipTitle("1M change");
                    break;
                }

                const scaledValue = scaleValue(item.performanceValue);

                return {
                  scaledValue: scaledValue,
                  value: item.performanceValue,
                  name: item.currencyConfig.code,
                  logo: item.currencyConfig.logoUrl,
                  sym: item.currencyConfig.code,
                  link: `/coin/${item.currencyConfig.code}`,
                  label: labelStyles,
                  change,
                  itemStyle: {
                    color: percentageChecker(change ?? 0, timeFrame),
                  },
                };
              });

            return {
              value: sectorValue,
              name: item.name,
              children: [...sectorData],
            };
          },
        );

        if (!sectorSpilt) {
          return {
            name: name,
            value: performanceValue,
            children: [...coinBreakdownData]
              .filter((obj: any) => obj.children.length > 0)
              .filter((obj: any) => !hideCoins.includes(obj.name)),
          };
        }
        return {
          name: name,
          value: performanceValue,
          children: [...coinData].filter((obj) => !hideCoins.includes(obj.name)),
        };
      });
      setDropDownData(newTreeMapData);
      setTreeMapData(newTreeMapData);
    }

    return () => {};
  }, [data, performance, timeFrame, hideCoins, sectorSpilt, filterValue]);

  useEffect(() => {
    setToolTipSubTitle("Market Cap");
  }, []);
  // ------------------TreeMap Options---------------------------
  const labelStyles = {
    position: "inside",
    align: "center",
    fontWeight: "bold",
    textShadow: "0px 0px 5px black",
    textBorderRadius: 2,
    formatter: function (params: any) {
      if (params.name && params.data) {
        return [`${params.name}`, `${params.data.change?.toFixed(2) ?? ""}%`].join("\n");
      }
    },
  };
  // Echarts options refer to echarts docs for all option types
  const options = {
    tooltip: {
      formatter: function (params: any) {
        if (!params.data.logo) {
          return [
            `${params.name}`,
            `<br>`,
            `${toolTipSubTitle} ${
              params.data.value.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              }) ?? ""
            }`,
          ].join("\n");
        }
        return [
          `${
            params.data.logo && `<img src=${params.data.logo} alt="logo" width="15" height="15"/>`
          }`,
          `${params.name} `,

          `<br>`,
          `${toolTipSubTitle} ${
            params.data.value.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }) ?? ""
          }`,
          `<br>`,
          `${toolTipTitle} ${params.data.change?.toFixed(2) ?? ""}%`,
        ].join("\n");
      },
      backgroundColor: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      extraCssText: "color: white",
    },
    series: {
      type: "treemap",
      squareRatio: 0.5 * (1 + Math.sqrt(5)),
      visibleMin: 100,
      top: 0,
      bottom: 0,
      width: "100%",
      aria: {
        enabled: true,
        decal: {
          show: true,
        },
      },
      roam: "move",

      breadcrumb: {
        show: roamingEnabled ? true : false,
        height: 20,
        itemStyle: {
          color: theme.palette.primary.main,

          textStyle: {
            fontSize: 100,
            color: "#fff",
          },
        },
      },
      nodeClick: roamingEnabled ? "zoomToNode" : "link",
      zoomToNodeRatio: 0.35,
      labelLayout: function (params: any) {
        const { rect } = params;
        const minSize = Math.min(rect.width, rect.height);
        let fontSize = Math.max(Math.sqrt(rect.width * rect.height) / 8.5, 12);

        if (minSize < 5) {
          return { fontSize: 0 };
        }

        // Create a temporary canvas context to measure text dimensions
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (ctx) {
          let textWidth, textHeight;
          do {
            ctx.font = `${fontSize}px Arial`; // Use the same font as in the treemap
            textWidth = ctx.measureText(params.name).width;
            textHeight = fontSize; // Approximate text height as the font size
            fontSize -= 1; // Decrease font size
          } while ((textWidth > rect.width || textHeight > rect.height) && fontSize > 0);
        }

        if (fontSize <= 2) {
          return { fontSize: 0 };
        }

        return {
          x: rect.x + rect.width / 2,
          y: rect.y + rect.height / 2,
          fontSize: fontSize,
          align: "center",
          verticalAlign: "middle",
          textAlign: "center",
          textVerticalAlign: "middle",
        };
      },
      data: treeMapData ? treeMapData.filter((obj: TreeMapData) => obj.children.length > 0) : [],
      levels: getLevelOption(),
    },
  };

  return (
    <>
      <Container sx={{ marginBottom: 1 }}>
        <MarketsTreemapExplainer />
        <MarketTreeMapToolBar
          data={dropDownData}
          drawerOpen={drawerOpen}
          setsDrawerOpen={setDrawerOpen}
          roaming={roamingEnabled}
          roamingSet={setRoamingEnabled}
          sectorSplit={sectorSpilt}
          sectorSplitSet={setSectorSplit}
        />
      </Container>
      {isLoading ? (
        <TreeMapSkelton />
      ) : (
        <>
          <Stack
            sx={{ width: "100%" }}
            direction={{ xs: "column", sm: "column", md: "row" }}
            justifyContent="space-between"
            alignItems="center">
            <MarketTreeMapLegend timeframe={timeFrame} />
            <Stack direction="row" spacing={2} alignItems="center">
              <TreemapScreenshotButton echartRef={echartRef} />
              {!drawerOpen && (
                <Tooltip title={settingsDesc}>
                  <Stack
                    sx={{
                      cursor: "pointer",
                      marginTop: { xs: "20px", sm: "20px", md: "0px" },
                    }}
                    alignItems="center"
                    direction="row"
                    onClick={() => setDrawerOpen(!drawerOpen)}>
                    <Typography
                      onClick={() =>
                        trackEvent("Select_Market_Sectors_Page", {
                          option: "Advanced_Options",
                        })
                      }
                      mr={{ sm: 1, xs: 1, md: 0 }}>
                      Advanced Options
                    </Typography>
                    <SettingsIcon />
                  </Stack>
                </Tooltip>
              )}
            </Stack>
          </Stack>
          <ReactEcharts ref={echartRef} option={options} style={{ height: "70vh" }} />
        </>
      )}
    </>
  );
};

export default MarketTreeMap;
